import {
  equipmentRequestAnswer,
  EquipmentRequestAnswerEvent,
  isEquipmentRequestAnswerEvent,
  isNewEquipmentRequestEvent,
  isOpenEventSocialsEvent,
  isOpenEventWebsiteEvent,
  isOpenExponentListEvent,
  newEquipmentRequest,
  NewEquipmentRequestEvent,
  openEventSocials,
  OpenEventSocialsEvent,
  openEventWebsite,
  OpenEventWebsiteEvent,
  openExponentList,
  OpenExponentListEvent,
} from "./appEvents";
import {
  addExponentsToEvent,
  AddExponentsToEventEvent,
  isAddExponentsToEventEvent,
} from "./appEvents/addExponentsToEvent.types";
import {
  appMakerEventRequestAnswer,
  AppMakerEventRequestAnswerEvent,
  isAppMakerEventRequestAnswerEvent,
} from "./appEvents/appMakerEventRequestAnswerEvent.types";
import {
  assignBadges,
  AssignBadgesEvent,
  isAssignBadgesEvent,
} from "./appEvents/assignBadges.types";
import {
  assignTickets,
  AssignTicketsEvent,
  isAssignTicketsEvent,
} from "./appEvents/assignTickets.types";
import {
  commercialisationCommentTag,
  CommercialisationCommentTagEvent,
  isCommercialisationCommentTagEvent,
} from "./appEvents/commercialisation";
import {
  isOpenEventDetailsEvent,
  openEventDetails,
  OpenEventDetailsEvent,
} from "./appEvents/event/openEventDetails.types";
import {
  exhibitorTicketAlertThreshold,
  ExhibitorTicketAlertThresholdEvent,
} from "./appEvents/exhibitorTicketAlertThreshold.types";
import {
  isOpenExponentConversationEvent,
  openExponentConversation,
  OpenExponentConversationEvent,
} from "./appEvents/exponent/openExponentConversation.types";
import {
  isOpenExponentProfileEvent,
  openExponentProfile,
  OpenExponentProfileEvent,
} from "./appEvents/exponent/openExponentProfile.types";
import {
  isOpenExponentSocialsEvent,
  openExponentSocials,
  OpenExponentSocialsEvent,
} from "./appEvents/exponent/openExponentSocials.types";
import {
  isOpenExponentWebsiteEvent,
  openExponentWebsite,
  OpenExponentWebsiteEvent,
} from "./appEvents/exponent/openExponentWebsite.types";
import {
  isValidateExponentActivityEvent,
  validateExponentActivity,
  ValidateExponentActivityEvent,
} from "./appEvents/exponent/validateExponentActivity.types";
import {
  exponentParticipationRequest,
  ExponentParticipationRequestEvent,
  isExponentParticipationRequestEvent,
} from "./appEvents/exponentParticipationRequest.types";
import {
  exponentParticipationRequestAnswer,
  ExponentParticipationRequestAnswerEvent,
  isExponentParticipationRequestAnswerEvent,
} from "./appEvents/exponentParticipationRequestAnswer.types";
import {
  isIssueBadgesEvent,
  issueBadges,
  IssueBadgesEvent,
} from "./appEvents/issueBadges.types";
import {
  isIssueTicketsEvent,
  issueTickets,
  IssueTicketsEvent,
} from "./appEvents/issueTickets.types";
import {
  isNewEventAsVisitorEvent,
  newEventAsVisitor,
  NewEventAsVisitorEvent,
} from "./appEvents/newEventAsVisitor.types";
import {
  isNewEventToControlEvent,
  newEventToControl,
  NewEventToControlEvent,
} from "./appEvents/newEventToControl.types";
import {
  isNewStatusEvent,
  NewStatusEvent,
  newStatusEvent,
} from "./appEvents/newStatusEvent.types";
import {
  isOpenOrganizerConversationEvent,
  openOrganizerConversation,
  OpenOrganizerConversationEvent,
} from "./appEvents/organizer/openOrganizerConversation.types";
import {
  isProPaymentMethodAcceptedEvent,
  proPaymentMethodAccepted,
  ProPaymentMethodAcceptedEvent,
} from "./appEvents/proPaymentMethodAccepted.types";
import {
  isProPaymentMethodRefusedEvent,
  proPaymentMethodRefused,
  ProPaymentMethodRefusedEvent,
} from "./appEvents/proPaymentMethodRefused.types";
import {
  isTicketAlertThresholdEvent,
  ticketAlertThreshold,
  TicketAlertThresholdEvent,
} from "./appEvents/ticketAlertThreshold.types";
import {
  isTransferedEventOwnershipEvent,
  TransferedEventOwnershipEvent,
  transferEventOwnership,
} from "./appEvents/transferEventOwnership.types";
import {
  buyProductAsVisitor,
  BuyProductAsVisitorEvent,
  isBuyProductAsVisitorEvent,
} from "./appEvents/visitor/buyProducts.types";
import {
  isVisitorEntryNotificationEvent,
  visitorEntryNotification,
  VisitorEntryNotificationEvent,
} from "./appEvents/visitorEntryNotification.types";
import {
  isVisitorFavoriteNotificationEvent,
  visitorFavoriteNotification,
  VisitorFavoriteNotificationEvent,
} from "./appEvents/visitorFavoriteNotification.types";

export const trackedEvents = {
  // Exponent events
  openExponentProfile,
  openExponentWebsite,
  openExponentSocials,
  openExponentConversation,
  openExponentList,
  // Organizer events
  openOrganizerConversation,
  // Event events
  openEventDetails,
  openEventWebsite,
  openEventSocials,
  // Notification events
  validateExponentActivity,
  issueTickets,
  issueBadges,
  assignTickets,
  assignBadges,
  addExponentsToEvent,
  exponentParticipationRequestAnswer,
  exponentParticipationRequest,
  appMakerEventRequestAnswer,
  newEventToControl,
  newEventAsVisitor,
  newEquipmentRequest,
  equipmentRequestAnswer,
  buyProductAsVisitor,
  transferEventOwnership,
  newStatusEvent,
  ticketAlertThreshold,
  exhibitorTicketAlertThreshold,
  proPaymentMethodAccepted,
  proPaymentMethodRefused,
  commercialisationCommentTag,
  visitorEntryNotification,
  visitorFavoriteNotification,
} as const;

export type TrackedEventName =
  (typeof trackedEvents)[keyof typeof trackedEvents];

export type AppEvent<TName extends TrackedEventName, TData = unknown> = {
  name: TName;
  data: TData;
};

export type AnyAppEvent = AppEvent<TrackedEventName>;

export type ExhibitorAppEvents =
  | OpenExponentProfileEvent
  | OpenExponentWebsiteEvent
  | OpenExponentSocialsEvent
  | OpenExponentConversationEvent
  | OpenExponentListEvent;
export const isExhibitorAppEvents = (
  event: AnyAppEvent
): event is ExhibitorAppEvents =>
  isOpenExponentProfileEvent(event) ||
  isOpenExponentWebsiteEvent(event) ||
  isOpenExponentSocialsEvent(event) ||
  isOpenExponentConversationEvent(event) ||
  isOpenExponentListEvent(event);

export type OrganizerAppEvents = OpenOrganizerConversationEvent;
export const isOrganizerAppEvents = (
  event: AnyAppEvent
): event is OrganizerAppEvents =>
  isOpenOrganizerConversationEvent(event) ||
  isTransferedEventOwnershipEvent(event);

export type EventAppEvents =
  | OpenEventDetailsEvent
  | OpenEventWebsiteEvent
  | OpenEventSocialsEvent;
export const isEventAppEvents = (event: AnyAppEvent): event is EventAppEvents =>
  isOpenEventDetailsEvent(event) ||
  isOpenEventWebsiteEvent(event) ||
  isOpenEventSocialsEvent(event);

export type NotificationAppEvents =
  | IssueTicketsEvent
  | IssueBadgesEvent
  | ValidateExponentActivityEvent
  | AddExponentsToEventEvent
  | ExponentParticipationRequestAnswerEvent
  | ExponentParticipationRequestEvent
  | AppMakerEventRequestAnswerEvent
  | AssignTicketsEvent
  | AssignBadgesEvent
  | NewEventToControlEvent
  | NewEventAsVisitorEvent
  | NewEquipmentRequestEvent
  | BuyProductAsVisitorEvent
  | EquipmentRequestAnswerEvent
  | TransferedEventOwnershipEvent
  | NewStatusEvent
  | TicketAlertThresholdEvent
  | ExhibitorTicketAlertThresholdEvent
  | ProPaymentMethodAcceptedEvent
  | ProPaymentMethodRefusedEvent
  | CommercialisationCommentTagEvent
  | VisitorEntryNotificationEvent
  | VisitorFavoriteNotificationEvent;

export const isNotificationAppEvents = (
  event: AnyAppEvent
): event is NotificationAppEvents =>
  isIssueTicketsEvent(event) ||
  isIssueBadgesEvent(event) ||
  isValidateExponentActivityEvent(event) ||
  isAddExponentsToEventEvent(event) ||
  isExponentParticipationRequestAnswerEvent(event) ||
  isExponentParticipationRequestEvent(event) ||
  isAppMakerEventRequestAnswerEvent(event) ||
  isAssignBadgesEvent(event) ||
  isAssignTicketsEvent(event) ||
  isNewEventAsVisitorEvent(event) ||
  isNewEventToControlEvent(event) ||
  isNewEquipmentRequestEvent(event) ||
  isBuyProductAsVisitorEvent(event) ||
  isEquipmentRequestAnswerEvent(event) ||
  isCommercialisationCommentTagEvent(event) ||
  isNewStatusEvent(event) ||
  isTicketAlertThresholdEvent(event) ||
  isProPaymentMethodAcceptedEvent(event) ||
  isProPaymentMethodRefusedEvent(event) ||
  isVisitorEntryNotificationEvent(event) ||
  isVisitorFavoriteNotificationEvent(event);

export type AllAppEvents =
  | ExhibitorAppEvents
  | OrganizerAppEvents
  | EventAppEvents
  | NotificationAppEvents;
